import { isValidatableControl, stringEquals } from "$lib/core";
import { ValidationService } from "aspnet-client-validation";
import { baylorFormClass } from "../core";
import { getProgressWizard } from "../multiStep";

/**
 * Customizes the aspnet-client-validation ValidationService to handle Baylor United forms.
 * These forms rely exclusively on the Constraints Validation API and thus do not use
 * validation summaries or validation spans to convey errors to users. Instead, this
 * service responds to validation failures by generating a centralized store of form control errors.
 * There may be multiple errors for any one control.
 */
// @ts-ignore
export class BuValidationService extends ValidationService {
  /**
   * 
   * @param {import("aspnet-client-validation").Logger?} logger 
   */
  constructor(logger = null) {
    super(logger ?? undefined);
    
    // CSS tracks each control's validation state using the `:user-invalid` pseudostate.
    // There is no need for additional classes for valid/invalid controls.
    this.ValidationInputCssClassName = "";
    this.ValidationInputValidCssClassName = "";
    
    /** @type {Map<string, import("./public").ErrorEntry[]>} */
    this.errors = new Map();
  }

  /**
   * @param {ParentNode} root 
   * @param {(input: import("./public").ValidatableElement) => void} cb 
   */
  scanInputs(root, cb) {
    let inputs = /** @type {import("./public").ValidatableElement[]} */ ([...root.querySelectorAll(`:scope [data-val="true"]`)].filter(isValidatableControl));

    // querySelectorAll does not include the root element itself.
    // we could use 'matches', but that's newer than querySelectorAll so we'll keep it simple and compatible.
    if (isValidatableControl(root) && root.getAttribute("data-val") === "true") {
      inputs.push(root);
    }

    for (const input of inputs) {
      cb.call(this, input);
    }
  }
  
  /**
   * Updates the core implementation so that elements with a `data-uid` attribute use that UID
   * rather than always generating a new UID.
   * 
   * @param {HTMLElement} node 
   * @returns 
   */
  getElementUID(node) {
    /** @type {{ node: HTMLElement, uid: string }[]} */
    // @ts-ignore
    const uids = this.elementUIDs;
    
    const registerUID = (/** @type {string} */ uid) => {
      uids.push({
        node: node,
        uid: uid
      });
      // @ts-ignore
      this.elementByUID[uid] = node;
    }
    
    let uid = node.dataset.uid;
    
    let x = uids.filter(e => {
      return e.node === node;
    })[0];
    
    // If have a UID via data-uid attribute,
    // ensure it is registered before returning.
    if (uid) {
      if (!x) {
        registerUID(uid);
      }
      return uid;
    }
    
    if (x) {
      return x.uid;
    }
    
    // No UID found. Generate a new one, register, and then return it.
    // @ts-ignore
    uid = /** @type {string} */ (this.guid4());
    registerUID(uid);
    return uid;
  }
  
  /**
   * Adds an error message to an input element, which also updates the validation message elements and validation summary elements.
   * @param {import("./public").ValidatableElement} element
   * @param {string} message The error message to show the user.
   * @param {string|null} [code=null] A unique machine_name that identifies the type of error.
   * @param {string|null} [source=null] The API contributing this error.
   */
  addError(element, message, code = null, source = null) {
    super.addError(/** @type {import("aspnet-client-validation").ValidatableElement} */ (element), message);

    if (code === null) return;
    
    // @ts-ignore
    const uid = this.getElementUID(element);
    
    let entries = this.errors.get(uid);
    if (entries === undefined) {
      entries = [];
      this.errors.set(uid, entries);
    }

    entries.push({
      message,
      source,
      code
    });
  }
  
  /**
   * Deletes errors related to the given element. If `code` is not specified, only the base class's logic is executed.
   * 
   * @param {import("./public").ValidatableElement} element 
   * @param {string|null} [code=null] The unique machine_name of the error to delete.
   * @param {string|null} [source=null] The API that contributed the error to delete. If specified without `code`, all errors related to `source` will be deleted.
   */
  removeError(element, code = null, source = null) {
    super.removeError(/** @type {import("aspnet-client-validation").ValidatableElement} */ (element));
    
    // @ts-ignore
    const uid = this.getElementUID(element);
    
    let entries = this.errors.get(uid);
    if (entries === undefined) {
      return;
    }
    
    if (source !== null) {
      if (code === null) {
        entries = entries.filter(err => stringEquals(err.source, source));
        this.errors.set(uid, entries);
        return;
      } else {
        const index = entries.findIndex(err => stringEquals(err.source, source) && stringEquals(err.code, code));
        entries.splice(index, 1);
      }
    } else {
      entries = [];
      this.errors.set(uid, entries);
    }

    // If this API believes there are no errors, but a custom error remains according to the Constraints Validation API,
    // then remove it.
    if (entries.length === 0 && !element.validity.valid && element.validity.customError) {
      element.setCustomValidity("");
    }
  }

  ///**
  // * Returns a validation Promise factory for an input element, using given validation directives.
  // * @param {import("./public").ValidatableElement} input
  // * @param {import("aspnet-client-validation").ValidationDirective} directives
  // */
  //createValidator(input, directives) {
  //  const hasValue = isValidatable(input);
  //  return async () => {
  //    // only validate visible and enabled fields
  //    // @ts-ignore
  //    if (!this.isHidden(input) && !this.isDisabled(input)) {
  //      for (let key in directives) {
  //        let directive = directives[key];
  //        // @ts-ignore
  //        let provider = this.providers[key];

  //        if (!provider) {
  //          // @ts-ignore
  //          this.logger.log('aspnet-validation provider not implemented: %s', key);
  //          continue;
  //        }
  //        // @ts-ignore
  //        this.logger.log("Running %s validator on element", key, input);

  //        // Don't allow this system to automatically evaluate custom elements without a `value` property that may be registered with it.
  //        // They will handle adding their own errors if they need to.
  //        let result = hasValue ? provider(input.value, input, directive.params) : true;
  //        let valid = false;
  //        let error = directive.error;

  //        if (typeof result === 'boolean') {
  //            valid = result;
  //        } else if (typeof result === 'string') {
  //            valid = false;
  //            error = result;
  //        } else {
  //            let resolution = await result;
  //            if (typeof resolution === 'boolean') {
  //                valid = resolution;
  //            } else {
  //                valid = false;
  //                error = resolution;
  //            }
  //        }

  //        if (!valid) {
  //            this.addError(input, error, key);
  //            return false;
  //        }
  //      }
  //    }

  //    this.removeError(input);
  //    return true;
  //  };
  //}

  /**
   * Adds an input element to be managed and validated by the service.
   * Triggers a debounced live validation when input value changes.
   * @param {import("./public").ValidatableElement} input
   */
  addInput(input) {
      let uid = this.getElementUID(input);

      let directives = this.parseDirectives(input.attributes);
      // @ts-ignore
      this.validators[uid] = this.createValidator(input, directives);

      if (input.form) {
          // @ts-ignore
          this.trackFormInput(input.form, uid);
      }

      // @ts-ignore
      if (this.inputEvents[uid]) {
          return;
      }

      /**
       * 
       * @param {Event} event 
       * @param {import("aspnet-client-validation").ValidatedCallback} callback 
       * @returns {Promise<boolean>}
       */
      const cb = async (event, callback) => {
          // @ts-ignore
          let validate = this.validators[uid];
          if (!validate) return true;
          
          if (
              !input.dataset.valEvent &&
              event && event.type === 'input' &&
              !input.classList.contains(this.ValidationInputCssClassName)
          ) {
              // When no data-val-event specified on a field, "input" event only takes it back to valid. "Change" event can make it invalid.
              return true;
          }

          // @ts-ignore
          this.logger.log('Validating', { event });
          try {
              const success = await validate();
              if (typeof(callback) === "function") {
                callback(success);
              }
              return success;
          }
          catch (error) {
              // @ts-ignore
              this.logger.log('Validation error', error);
              return false;
          }
      };

      /** @type {NodeJS.Timeout | null} */
      let debounceTimeoutID = null;

      /**
       * 
       * @param {Event} event 
       * @param {import("aspnet-client-validation").ValidatedCallback} callback 
       * @returns {void}
       */
      cb.debounced = (event, callback) => {
        if (debounceTimeoutID !== null) {
          clearTimeout(debounceTimeoutID);
        }
        debounceTimeoutID = setTimeout(() => {
          cb(event, callback);
        }, this.debounce);
      };

      const defaultEvent = input instanceof HTMLSelectElement ? 'change' : 'input change';
      const validateEvent = input.dataset.valEvent ?? defaultEvent;
      const events = validateEvent.split(' ');

      const handler = (/** @type {Event} */ ev) => {
        cb.debounced(ev, success => {
          this.inputValidationHandler(input, success);
        });
      };

      events.forEach((eventName) => {
        input.addEventListener(eventName, handler);
      });

      cb.remove = () => {
        events.forEach((eventName) => {
          input.removeEventListener(eventName, handler);
        });
      };

      // @ts-ignore
      this.inputEvents[uid] = cb;
  }

  /**
   * @param {import("./public").ValidatableElement} input 
   * @param {boolean} success 
   */
  inputValidationHandler(input, success) {
    // @ts-ignore
    if (input.form?.classList.contains(this.options.formClass) ?? false) {
      if (!success && !input.validationMessage) {
        const uid = this.getElementUID(input);
        // @ts-ignore
        const summary = this.summary[uid];
        if (summary) {
          input.setCustomValidity(summary);
        }
      } else if (success && !input.validity.valid && input.validity.customError) {
        input.setCustomValidity("");
      } 
    }
  }

  /**
   * @param {HTMLFormElement} form 
   */
  focusFirstInvalid = (form) => {
    let formUID = this.getElementUID(form);
    // @ts-ignore
    let formInputUIDs = /** @type {Record<string, string[]>} */ (this.formInputs)[formUID];
    // @ts-ignore
    let invalidFormInputUID = formInputUIDs?.find(uid => this.summary[uid]);

    if (invalidFormInputUID) {
      // @ts-ignore
      if (form.classList.contains(this.options.formClass)) {
        // @ts-ignore
        const element = this.elementByUID[invalidFormInputUID];
        const wizard = getProgressWizard(form);
        if (wizard) {
          wizard.navigateToStepWithElement(element, async ev => {
            form.reportValidity();
          });
        } else {
          form.reportValidity();
        }
      } else {
        // Original logic from base class.
        // @ts-ignore
        const firstInvalid = this.elementByUID[invalidFormInputUID];
        if (firstInvalid instanceof HTMLElement) {
            firstInvalid.focus();
        }
      }
    }
  }

  /**
   * Adds addClass and removes removeClass
   * @param {Element} element Element to modify
   * @param {string} addClass Class to add
   * @param {string} removeClass Class to remove
   */
  swapClasses(element, addClass, removeClass) {
    // @ts-ignore
    const isBaylorForm = (/** @type {Element & { form?: HTMLFormElement }} */ (element)).form?.classList.contains(this.options.formClass) ?? false;
    if (isBaylorForm) {
      return;
    }
    // @ts-ignore
    if (addClass && !this.isDisabled(element) && !element.classList.contains(addClass)) {
        element.classList.add(addClass);
    }
    if (element.classList.contains(removeClass)) {
        element.classList.remove(removeClass);
    }
  }

  /**
   * Load default validation providers and scans the entire document when ready.
   * @param {Partial<import("aspnet-client-validation").ValidationServiceOptions>?} options
   */
  bootstrap(options = null) {
    /** @type {import("./public").BuValidationServiceOptions} */
    const defaultOptions = {
      root: document.body,
      watch: false,
      addNoValidate: true,
      formClass: baylorFormClass,
    }

    super.bootstrap(options ?? defaultOptions);
  }

}

export const validationServiceInstance = new BuValidationService();
